import type { DrawingLine, NormalizedPoint } from '../model';
import type { DrawingAtom } from './graphDrawing.ts';
import type { PaperSize } from './geometry.ts';
import {
  ATOM_ATTACH_DISTANCE_PX,
  distanceBetween,
  nearestAtomWithin,
  normalizedToPixel,
  type PixelPoint
} from './bonds.ts';
import { distanceToSegment } from '../rendering/chemicalGeometry.ts';

// Arbitrary-size cycles (PDF requirement, beyond the 3-8-membered + benzene presets): the geometry
// below is already parameterized by size for any N -- MIN/MAX only bound the UI's custom-size
// input, not the math. MAX_RING_SIZE is a practical cap, not a chemical one: circumradius grows as
// bondLength / (2*sin(pi/N)), so very large N would place atoms far enough apart to overflow a
// normal-sized drawing paper well before they'd overflow the polygon math itself.
export const MIN_RING_SIZE = 3;
export const MAX_RING_SIZE = 20;

export interface RingTemplate {
  readonly size: number;
  readonly aromatic: boolean;
  // Ring-edge indices (0-based on the ring's own edge list) drawn as double bonds -- the
  // cycloalkene / cyclodiene templates. Ignored when `aromatic`. Edge 0 is never listed, so
  // fusing a template onto an existing bond leaves that bond's order alone.
  readonly doubleBondEdges?: readonly number[];
}

export type CycleTemplateId = 'cyclopentene' | 'cyclopentadiene' | 'cyclohexene' | 'cyclohexadiene' | 'benzene-kekule';

// The unsaturated ring templates the left-rail Cycles group offers beyond the plain n-gons
// (#252). Saturated rings and aromatic benzene stay on the `ring-${n}` / `benzene` tools.
export const CYCLE_TEMPLATES: Record<CycleTemplateId, RingTemplate> = {
  cyclopentene: { size: 5, aromatic: false, doubleBondEdges: [1] },
  cyclopentadiene: { size: 5, aromatic: false, doubleBondEdges: [1, 3] },
  cyclohexene: { size: 6, aromatic: false, doubleBondEdges: [1] },
  cyclohexadiene: { size: 6, aromatic: false, doubleBondEdges: [1, 3] },
  'benzene-kekule': { size: 6, aromatic: false, doubleBondEdges: [1, 3, 5] }
};

export function cycleTemplate(id: string): RingTemplate | null {
  return Object.prototype.hasOwnProperty.call(CYCLE_TEMPLATES, id) ? CYCLE_TEMPLATES[id as CycleTemplateId] : null;
}

export interface RingTemplatePlacement {
  readonly lines: readonly DrawingLine[];
  readonly sharedBondId?: string;
}

function rotate(point: PixelPoint, radians: number): PixelPoint {
  return {
    x: point.x * Math.cos(radians) - point.y * Math.sin(radians),
    y: point.x * Math.sin(radians) + point.y * Math.cos(radians)
  };
}

function line(id: string, start: PixelPoint, end: PixelPoint, size: PaperSize): DrawingLine {
  return {
    id,
    pointerType: 'mouse',
    createdAt: new Date().toISOString(),
    start: { x: start.x / size.width, y: start.y / size.height },
    end: { x: end.x / size.width, y: end.y / size.height }
  };
}

function regularPolygon(center: PixelPoint, count: number, radius: number, startAngle: number): PixelPoint[] {
  return Array.from({ length: count }, (_, index) => ({
    x: center.x + Math.cos(startAngle + (index * 2 * Math.PI) / count) * radius,
    y: center.y + Math.sin(startAngle + (index * 2 * Math.PI) / count) * radius
  }));
}

export function createRingTemplatePlacement(
  pointer: NormalizedPoint,
  template: RingTemplate,
  bondLength: number,
  size: PaperSize,
  atoms: readonly DrawingAtom[],
  existingLines: readonly DrawingLine[]
): RingTemplatePlacement {
  const pointerPixel = normalizedToPixel(pointer, size);
  const attachedAtom = nearestAtomWithin(pointerPixel, atoms, size, ATOM_ATTACH_DISTANCE_PX);
  const nearestBond = attachedAtom
    ? undefined
    : existingLines.reduce<{ line: DrawingLine; distance: number } | undefined>((nearest, candidate) => {
        const distance = distanceToSegment(
          pointerPixel,
          normalizedToPixel(candidate.start, size),
          normalizedToPixel(candidate.end, size)
        );
        return distance <= ATOM_ATTACH_DISTANCE_PX && (!nearest || distance < nearest.distance)
          ? { line: candidate, distance }
          : nearest;
      }, undefined);

  let points: PixelPoint[];
  let sharedBondId: string | undefined;
  if (nearestBond) {
    const start = normalizedToPixel(nearestBond.line.start, size);
    const end = normalizedToPixel(nearestBond.line.end, size);
    const side = distanceBetween(start, end);
    const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    const normal = { x: -(end.y - start.y) / side, y: (end.x - start.x) / side };
    const apothem = side / (2 * Math.tan(Math.PI / template.size));
    const candidates = [1, -1].map((sign) => ({
      x: midpoint.x + normal.x * apothem * sign,
      y: midpoint.y + normal.y * apothem * sign
    }));
    const otherAtoms = atoms.filter((atom) => {
      const position = normalizedToPixel(atom.position, size);
      return distanceBetween(position, start) > 0.5 && distanceBetween(position, end) > 0.5;
    });
    const existingCenter =
      otherAtoms.length > 0
        ? {
            x: otherAtoms.reduce((sum, atom) => sum + atom.position.x * size.width, 0) / otherAtoms.length,
            y: otherAtoms.reduce((sum, atom) => sum + atom.position.y * size.height, 0) / otherAtoms.length
          }
        : { x: size.width / 2, y: size.height / 2 };
    const center = candidates.reduce((best, candidate) =>
      distanceBetween(candidate, existingCenter) > distanceBetween(best, existingCenter) ? candidate : best
    );
    const startVector = { x: start.x - center.x, y: start.y - center.y };
    const step = (2 * Math.PI) / template.size;
    const positiveNext = rotate(startVector, step);
    const positiveDistance = distanceBetween({ x: center.x + positiveNext.x, y: center.y + positiveNext.y }, end);
    const direction = positiveDistance < 0.5 ? 1 : -1;
    points = Array.from({ length: template.size }, (_, index) => {
      const vector = rotate(startVector, direction * step * index);
      return { x: center.x + vector.x, y: center.y + vector.y };
    });
    sharedBondId = nearestBond.line.id;
  } else {
    const atom = attachedAtom;
    const radius = bondLength / (2 * Math.sin(Math.PI / template.size));
    if (atom) {
      const anchor = normalizedToPixel(atom.position, size);
      const otherAtoms = atoms.filter((candidate) => candidate.id !== atom.id);
      const structureCenter =
        otherAtoms.length > 0
          ? {
              x: otherAtoms.reduce((sum, candidate) => sum + candidate.position.x * size.width, 0) / otherAtoms.length,
              y: otherAtoms.reduce((sum, candidate) => sum + candidate.position.y * size.height, 0) / otherAtoms.length
            }
          : { x: size.width / 2, y: size.height / 2 };
      const angleAway = Math.atan2(anchor.y - structureCenter.y, anchor.x - structureCenter.x);
      const center = {
        x: anchor.x + Math.cos(angleAway) * radius,
        y: anchor.y + Math.sin(angleAway) * radius
      };
      points = regularPolygon(center, template.size, radius, angleAway + Math.PI);
      points[0] = anchor;
    } else {
      points = regularPolygon(pointerPixel, template.size, radius, -Math.PI / 2);
    }
  }

  return {
    lines: points.map((point, index) =>
      line(
        globalThis.crypto?.randomUUID?.() ?? `ring-${Date.now()}-${index}`,
        point,
        points[(index + 1) % points.length]!,
        size
      )
    ),
    ...(sharedBondId ? { sharedBondId } : {})
  };
}
